// ─── Enums ───────────────────────────────────────────────────────────────────

export const ACTIVITY_TYPE = {
  NEW_INVESTOR_SIGN_UP: 'NewInvestorSignup',
  INTRODUCTION_SHARED: 'IntroductionShared',
  INTRODUCTION_EXPIRED: 'IntroductionExpired',
  INTRODUCTION_NOT_ELIGIBLE_USER_IN_BONUS_PERIOD: 'IntroductionNotEligibleUserInBonusPeriod',
  INTRODUCTION_NOT_ELIGIBLE_ALREADY_EXISTS: 'IntroductionNotEligibleAlreadyExists',
  INVESTOR_ELIGIBILITY_EXPIRING_SOON: 'InvestorEligibilityExpiringSoon',
  INVESTOR_ELIGIBILITY_EXPIRED: 'InvestorEligibilityExpired',
  INVESTMENT: 'Investment',
  WITHDRAWAL: 'Withdrawal',
  NEW_INVESTOR_BASE_FEE: 'NewInvestorBaseFee',
  NEW_INVESTOR_BONUS_FEE: 'NewInvestorBonusFee',
  EXISTING_INVESTOR_BASE_FEE: 'ExistingInvestorBaseFee',
  MASTER_INTRODUCER_FEE: 'MasterIntroducerFee',
  MANUAL_ATTRIBUTION_GENERAL: 'ManualAttributionGeneral',
  MANUAL_ATTRIBUTION_FUND: 'ManualAttributionFund',
} as const;

export type ActivityType = (typeof ACTIVITY_TYPE)[keyof typeof ACTIVITY_TYPE];

export const INTRODUCTION_TYPE = {
  NEW: 'New',
  EXISTING: 'Existing',
} as const;

export type IntroductionType = (typeof INTRODUCTION_TYPE)[keyof typeof INTRODUCTION_TYPE];

export type SortDirection = 'asc' | 'desc';

// ─── Pagination ───────────────────────────────────────────────────────────────

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: PaginationMeta;
}

// ─── Shared ───────────────────────────────────────────────────────────────────

export interface FilterOption {
  id: string;
  name: string;
}

export interface ActivityMetadata {
  potentiallyEligibleAt?: string | null;
  isActive?: boolean | null;
  lastTransactionDate?: string | null;
  expiresAt?: string | null;
  manualAttributionAdminUserId?: string | null;
  manualAttributionAdminName?: string | null;
  manualAttributionOutcome?: ManualAttributionOutcome | null;
}

// ─── Screen 1: Introducer's Own Dashboard ────────────────────────────────────

export interface IntroducerProfile {
  referralCode: string;
  totalRewards: string;
  claimableRewards: string;
  claimedRewards: string;
  isAgreementSigned: boolean;
  isActiveIntroducer: boolean;
  memberSince: string | null;
  agreementDocumentId: string | null;
}

export interface FilterTypesRes {
  types: IntroductionType[];
  activities: ActivityType[];
}

export interface IntroducerDashboardInvestor {
  investorUserId: string;
  investorName: string;
  introductionType: IntroductionType;
  joinedAt: string;
  fundsCount: number;
  totalInvested: string;
  totalRewards: string;
  subReferralsCount: number;
}

export interface IntroducerDashboardFund {
  fundId: string;
  fundName: string;
  introducedAt: string;
  totalInvested: string;
  totalRewards: string;
  referredInvestorsCount: number;
  lastActivityAt: string | null;
}

export interface IntroducerDashboardActivity {
  id: string;
  type: ActivityType;
  activityDate: string;
  fundId: string | null;
  fundName: string | null;
  userId: string | null;
  userName: string | null;
  introducerUserId: string | null;
  introducerName: string | null;
  amount: string | null;
  units: string | null;
  metadata: ActivityMetadata | null;
  createdAt: string;
}

export interface ActivitiesResponse {
  items: IntroducerDashboardActivity[];
  total: number;
  page: number;
  limit: number;
}

export type InvestorSortBy =
  | 'investorName'
  | 'joinedAt'
  | 'fundsCount'
  | 'totalInvested'
  | 'totalRewards'
  | 'subReferralsCount';

export interface GetInvestorsParams {
  page?: number;
  limit?: number;
  search?: string;
  userIds?: string[];
  fundIds?: string[];
  types?: IntroductionType[];
  activityTypes?: ActivityType[];
  sortBy?: InvestorSortBy;
  sortDirection?: SortDirection;
}

export interface GetFundsParams {
  page?: number;
  limit?: number;
  search?: string;
  userIds?: string[];
  fundIds?: string[];
  types?: IntroductionType[];
  activityTypes?: ActivityType[];
  sortBy?: string;
  sortDirection?: SortDirection;
}

export interface GetActivitiesParams {
  page?: number;
  limit?: number;
  introductionId?: string;
  fundId?: string;
  investorUserId?: string;
  activityTypes?: ActivityType[];
}

export interface GetFilterOptionsParams {
  page?: number;
  limit?: number;
  search?: string;
}

// ─── Screen 2: Admin Dashboard ───────────────────────────────────────────────

export interface AdminIntroducerSummary {
  totalIntroducers: number;
  totalReferrals: number;
  totalInvested: string;
  totalRewardsEarned: string;
  totalRewardsClaimed: string;
  avgIntroducerReward: string;
}

export interface AdminIntroducerItem {
  id: string;
  name: string;
  email: string;
  joinedAt: string;
  referralsCount: number;
  subReferralsCount: number;
  fundsCount: number;
  totalInvested: string;
  rewardsEarned: string;
  rewardsClaimed: string;
}

export interface AdminIntroducersResponse {
  items: AdminIntroducerItem[];
  pagination: PaginationMeta;
  appliedFilters: Record<string, unknown>;
}

export interface AdminFundItem {
  id: string;
  name: string;
  introducersCount: number;
  referredInvestorsCount: number;
  totalInvested: string;
  rewardsGenerated: string;
  lastActivity: string | null;
  firstIntroduction: string | null;
}

export interface AdminFundsResponse {
  items: AdminFundItem[];
  pagination: PaginationMeta;
  appliedFilters: Record<string, unknown>;
}

export interface AdminFundFilters {
  introducersCount: { min: number; max: number };
  totalInvested: { min: number; max: number };
  rewardsGenerated: { min: number; max: number };
  activities: ActivityType[];
}

export type AdminIntroducerSortBy =
  | 'userName'
  | 'joinedAt'
  | 'referralsCount'
  | 'subReferralsCount'
  | 'fundsCount'
  | 'totalInvested'
  | 'rewardsEarned'
  | 'rewardsClaimed';

export type AdminFundSortBy =
  | 'fundName'
  | 'introducersCount'
  | 'referredInvestorsCount'
  | 'totalInvested'
  | 'rewardsGenerated'
  | 'firstIntroduction'
  | 'lastActivity';

export interface GetAdminIntroducersParams {
  page?: number;
  limit?: number;
  search?: string;
  introducerUserIds?: string[];
  fundIds?: string[];
  types?: IntroductionType[];
  activityTypes?: ActivityType[];
  minReferrals?: number;
  maxReferrals?: number;
  joinedAfter?: string;
  joinedBefore?: string;
  minTotalInvested?: string;
  maxTotalInvested?: string;
  minRewardsEarned?: string;
  maxRewardsEarned?: string;
  sortBy?: AdminIntroducerSortBy;
  sortDirection?: SortDirection;
}

export interface GetAdminFundsParams {
  page?: number;
  limit?: number;
  search?: string;
  minIntroducers?: number;
  maxIntroducers?: number;
  minTotalInvested?: string;
  maxTotalInvested?: string;
  minRewardsGenerated?: string;
  maxRewardsGenerated?: string;
  lastActivityAfter?: string;
  lastActivityBefore?: string;
  activityTypes?: ActivityType[];
  sortBy?: AdminFundSortBy;
  sortDirection?: SortDirection;
}

export interface AdminActivitiesBody {
  fundId?: string;
  introducerUserId?: string;
  investorUserId?: string;
  activityTypes?: ActivityType[];
  page?: number;
  limit?: number;
}

// ─── Manual Attribution ─────────────────────────────────────────────

export const ATTRIBUTION_TYPE = {
  GENERAL: 'General',
  FUND: 'Fund',
} as const;

export type AttributionType = (typeof ATTRIBUTION_TYPE)[keyof typeof ATTRIBUTION_TYPE];

export const MANUAL_ATTRIBUTION_OUTCOME = {
  INVITATION_SENT: 'InvitationSent',
  ATTRIBUTED_AS_NEW_INVESTOR: 'AttributedAsNewInvestor',
  ALREADY_HAS_INTRODUCER: 'AlreadyHasIntroducer',
  ALREADY_INVITED: 'AlreadyInvited',
  NOT_ELIGIBLE_BONUS_PERIOD: 'NotEligibleBonusPeriod',
  NOT_ELIGIBLE_ACTIVE_INTRODUCTION: 'NotEligibleActiveIntroduction',
  FUND_INTRODUCTION_ACTIVATED: 'FundIntroductionActivated',
  ALREADY_ACTIVE: 'AlreadyActive',
} as const;

export type ManualAttributionOutcome =
  (typeof MANUAL_ATTRIBUTION_OUTCOME)[keyof typeof MANUAL_ATTRIBUTION_OUTCOME];

export interface CreateManualAttributionBody {
  introducerEmail: string;
  investorEmail: string;
  attributionType: AttributionType;
  fundId?: string;
  inviteeName?: string;
}

export interface ManualAttributionResult {
  outcome: ManualAttributionOutcome;
  message: string;
}

/**
 * A Legal Entity the investor owns (is a shareholder of). On an A2/B2
 * attribution these entities follow the investor's attribution to the same
 * introducer. Sourced from the BFF ownership aggregation, not the mutation.
 */
export interface InvestorOwnedEntity {
  id: string;
  name: string;
  logoUrl: string | null;
}

export interface InvestorOwnedEntitiesResponse {
  entities: InvestorOwnedEntity[];
}

// ─── Investor Eligibility ─────────────────────────────────────────────

export interface InvestorEligibility {
  inBonusPeriodUntil: string | null;
  activeFundIntroductionExpiresAt: string | null;
  isEligible: boolean;
}
